import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import DialogDefault from "../../parameter/PortalDialogDefault";
import BarcodeDetectDialogFields from "../../field/barcode/BarcodeDetectDialogFields";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import {TabPanel, TabView} from "primereact/tabview";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import DocumentSettings from "../../../document/DocumentSettings";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import {MultiSelect} from "primereact/multiselect";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	Barcode,
	BarcodeDetectOutputFormat,
	BarcodeSelection,
	DetectBarcode,
	Functions,
	Information,
	Security,
	Settings,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalBarcode} from "../../../../shared/models/PortalBarcode";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface BarcodeDetectDialogStatus extends PortalDialogState<PortalBarcode> {
	pages: number;
	currentPage: string;
	panel: number;
}

enum BarcodeDetectFormatEnum {
	QRCODE = "qrcode",
	AZTEC = "aztec",
	CODABAR = "codabar",
	CODE128 = "code128",
	CODE39 = "code39",
	DATAMATRIX = "datamatrix",
	EAN13 = "ean13",
	EAN8 = "ean8",
	ITF = "itf",
	PDF417 = "pdf417",
	UPCA = "upca",
	ALL = "all"
}

export class BarcodeDetectDialogDefinition extends PortalDialog<PortalBarcode, PortalDialogProperties<PortalBarcode>,
	BarcodeDetectDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			panel: 0,
			fieldErrors: new Map<BarcodeDetectDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.barcode?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.detectBarcode, Functions.getDetectBarcodeDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.BARCODE_DETECT, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderGeneralTab(detectParameter: DetectBarcode): ReactNode {
		let selectionParameter: BarcodeSelection = detectParameter.selection![0];
		let errors: Map<BarcodeDetectDialogFields, string> = this.state.fieldErrors;

		let gs1Formats: Array<string> = [
			BarcodeDetectFormatEnum.QRCODE,
			BarcodeDetectFormatEnum.ITF,
			BarcodeDetectFormatEnum.DATAMATRIX,
			BarcodeDetectFormatEnum.EAN8,
			BarcodeDetectFormatEnum.EAN13,
			BarcodeDetectFormatEnum.UPCA,
			BarcodeDetectFormatEnum.CODE128
		];

		let isGs1Format: boolean = false;
		for (let gs1Format of gs1Formats) {
			if (selectionParameter.formats?.includes(gs1Format)) {
				isGs1Format = true;
				break;
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(BarcodeDetectDialogFields.OUTPUT)}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-output">
							<Translate {...this.props} data="barcode.detect.output.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown value={detectParameter.outputFormat} inputId="control-output"
									  options={[
										  {
											  label: Translate.value(
												  "barcode.detect.output.format.xml", {ns: "dialog"}
											  ),
											  value: BarcodeDetectOutputFormat.Xml
										  },
										  {
											  label: Translate.value(
												  "barcode.detect.output.format.json", {ns: "dialog"}
											  ),
											  value: BarcodeDetectOutputFormat.Json
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  BarcodeDetectDialogFields.OUTPUT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(BarcodeDetectDialogFields.OUTPUT)}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-format">
							<Translate {...this.props} data="barcode.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<MultiSelect display="chip" inputId="control-format"
										 value={FunctionHelper.applyValue(
											 selectionParameter.formats, BarcodeSelection.getFormatsDefault()
										 ).split(",")}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.FORMAT, e.value
										 )}
										 options={[
											 {
												 label: Translate.value(
													 "barcode.format.qrcode", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.QRCODE
											 },
											 {
												 label: Translate.value(
													 "barcode.format.aztec", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.AZTEC
											 },
											 {
												 label: Translate.value(
													 "barcode.format.codabar", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.CODABAR
											 },
											 {
												 label: Translate.value(
													 "barcode.format.code128", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.CODE128
											 },
											 {
												 label: Translate.value(
													 "barcode.format.code39", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.CODE39
											 },
											 {
												 label: Translate.value(
													 "barcode.format.datamatrix", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.DATAMATRIX
											 },
											 {
												 label: Translate.value(
													 "barcode.format.ean13", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.EAN13
											 },
											 {
												 label: Translate.value(
													 "barcode.format.ean8", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.EAN8
											 },
											 {
												 label: Translate.value(
													 "barcode.format.itf", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.ITF
											 },
											 {
												 label: Translate.value(
													 "barcode.format.pdf417", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.PDF417
											 },
											 {
												 label: Translate.value(
													 "barcode.format.upca", {ns: "dialog"}
												 ),
												 value: BarcodeDetectFormatEnum.UPCA
											 }
										 ]}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.barcode.format"} namespace={"help"} markdown={true}/>
					</Helper>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(BarcodeDetectDialogFields.OUTPUT)}>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(BarcodeDetectDialogFields.CONTENT_LENGTH)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-content-length">
							<Translate {...this.props} data="barcode.detect.length" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-content-length" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={parseInt(FunctionHelper.applyValue(
											 selectionParameter.allowedLengths,
											 BarcodeSelection.getAllowedLengthsDefault()
										 ))}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.CONTENT_LENGTH, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(BarcodeDetectDialogFields.PURE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pure"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-pure"
										 checked={FunctionHelper.applyValue(
											 selectionParameter.pureBarcode, BarcodeSelection.getPureBarcodeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.PURE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-pure">
								<Translate {...this.props} data="barcode.detect.pure.label" namespace="dialog"/>
								<Info {...this.props} data="barcode.detect.pure.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!isGs1Format && !this.state.fields?.contains(BarcodeDetectDialogFields.GS1)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-gs1"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-gs1"
										 checked={FunctionHelper.applyValue(
											 selectionParameter.gs1, BarcodeSelection.getGs1Default()
										 )}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.GS1, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-gs1">
								<Translate {...this.props} data="barcode.detect.gs1.label" namespace="dialog"/>
								<Info {...this.props} data="barcode.detect.gs1.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(BarcodeDetectDialogFields.PAGERANGE)}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="barcode.pages.label" namespace="dialog"/>
							<Info {...this.props} data="barcode.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(BarcodeDetectDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   selectionParameter.pages, BarcodeSelection.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   BarcodeDetectDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")} editable={true}
									   addon={Translate.value(
										   "inputs.pagerange.of", {pages: this.state.pages}
									   )}
									   tooltip={errors.has(BarcodeDetectDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(BarcodeDetectDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {
											   label: Translate.value("inputs.pagerange.all"),
											   value: PAGERANGE_ALL
										   },
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(BarcodeDetectDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(BarcodeDetectDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderExtendedTab(detectParameter: DetectBarcode): ReactNode {
		let selectionParameter: BarcodeSelection = detectParameter.selection![0];

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					(
						!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.CODE39) ||
						!this.state.fields?.contains(BarcodeDetectDialogFields.CHECK_DIGIT)) &&
					(
						!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.CODABAR) ||
						!this.state.fields?.contains(BarcodeDetectDialogFields.START_END_DIGITS)) &&
					(
						(
							!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.EAN8) &&
							!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.EAN13) &&
							!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.UPCA)
						) ||
						!this.state.fields?.contains(BarcodeDetectDialogFields.EXTENSION)
					)
				}>
					<div className="p-col-12 p-grid" hidden={
						!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.CODE39) ||
						!this.state.fields?.contains(BarcodeDetectDialogFields.CHECK_DIGIT)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-check-digit"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-check-digit"
										 checked={FunctionHelper.applyValue(
											 selectionParameter.barcode39CheckDigit,
											 BarcodeSelection.getBarcode39CheckDigitDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.CHECK_DIGIT, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-check-digit">
								<Translate {...this.props} data="barcode.detect.code39.check_digit.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.CODABAR) ||
						!this.state.fields?.contains(BarcodeDetectDialogFields.START_END_DIGITS)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-start-end-digits"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-start-end-digits"
										 checked={FunctionHelper.applyValue(
											 selectionParameter.codabarStartEndDigits,
											 BarcodeSelection.getCodabarStartEndDigitsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.START_END_DIGITS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-start-end-digits">
								<Translate {...this.props} data="barcode.detect.codabar.digits.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						(
							!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.EAN8) &&
							!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.EAN13) &&
							!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.UPCA)
						) ||
						!this.state.fields?.contains(BarcodeDetectDialogFields.EXTENSION)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-extension">
							<Translate {...this.props} data="barcode.detect.upc_ean.extension" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-extension" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={parseInt(FunctionHelper.applyValue(
											 selectionParameter.upcEanExtensions,
											 BarcodeSelection.getUpcEanExtensionsDefault()
										 ))}
										 onChange={(e) => this.onChange(
											 BarcodeDetectDialogFields.EXTENSION, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.detect?.selection)) {
			return null;
		}

		let detectParameter: DetectBarcode = parameter!.detect!;
		let selectionParameter: BarcodeSelection = detectParameter.selection![0];

		let extendedTab: boolean = (
			!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.CODABAR) &&
			!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.CODE39) &&
			!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.EAN8) &&
			!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.EAN13) &&
			!selectionParameter.formats?.includes(BarcodeDetectFormatEnum.UPCA)
		);

		return (
			<>
				{extendedTab ? this.renderGeneralTab(detectParameter) :
					<TabView activeIndex={this.state.panel} onTabChange={
						(e) => this.setState({panel: e.index})
					}>
						<TabPanel header={Translate.value("barcode.detect.tabs.general", {ns: "dialog"})}>
							{this.renderGeneralTab(detectParameter)}
						</TabPanel>
						<TabPanel header={Translate.value("barcode.detect.tabs.extended", {ns: "dialog"})}>
							{this.renderExtendedTab(detectParameter)}
						</TabPanel>
					</TabView>
				}
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["detect-barcode-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.barcode.detect.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.barcode.detect.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: BarcodeDetectDialogFields, value: any): void {
		let parameter: PortalBarcode = this.state.parameter!.data;
		let detectParameter: DetectBarcode = parameter.detect!;
		let selectionParameter: BarcodeSelection = detectParameter.selection![0];
		let errors: Map<BarcodeDetectDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case BarcodeDetectDialogFields.OUTPUT:
				detectParameter.outputFormat = value;
				break;
			case BarcodeDetectDialogFields.FORMAT:
				selectionParameter.formats = value.length > 0 ? value.join(",") : BarcodeDetectFormatEnum.QRCODE;
				break;
			case BarcodeDetectDialogFields.CONTENT_LENGTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BarcodeSelection.getAllowedLengthsDefault();
				}

				selectionParameter.allowedLengths = value.toString();
				break;
			case BarcodeDetectDialogFields.PURE:
				selectionParameter.pureBarcode = value;
				break;
			case BarcodeDetectDialogFields.GS1:
				selectionParameter.gs1 = value;
				break;
			case BarcodeDetectDialogFields.CHECK_DIGIT:
				selectionParameter.barcode39CheckDigit = value;
				break;
			case BarcodeDetectDialogFields.EXTENSION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BarcodeSelection.getUpcEanExtensionsDefault();
				}

				selectionParameter.upcEanExtensions = value.toString();
				break;
			case BarcodeDetectDialogFields.START_END_DIGITS:
				selectionParameter.codabarStartEndDigits = value;
				break;
			case BarcodeDetectDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				selectionParameter.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-barcode-detect"}/>
					<Translate {...this.props} data="barcode.detect.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBarcode | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		this.setState({
			panel: 0,
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			currentPage: currentPage,
			pages: pageCount
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalBarcode = this.state.parameter!.data.clone();

		if (parseInt(operationData.detect!.selection![0].allowedLengths!) === 0) {
			operationData.detect!.selection![0].allowedLengths = BarcodeSelection.getAllowedLengthsDefault();
		}

		if (parseInt(operationData.detect!.selection![0].upcEanExtensions!) === 0) {
			operationData.detect!.selection![0].upcEanExtensions = BarcodeSelection.getUpcEanExtensionsDefault();
		}

		let webServiceType: WebServiceType = WebServiceTypes.BARCODE;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: Barcode.fromJson(operationData.toJson()),
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.barcode.detect", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.resetMouseMode(documentManager.getSelection());
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BARCODE_DETECT;
	}

	protected getDefaultParameter(): Parameter<PortalBarcode> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		})
	}
}

const BarcodeDetectDialog = React.memo(BarcodeDetectDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalBarcode>>,
		nextProps: Readonly<PortalDialogProperties<PortalBarcode>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BarcodeDetectDialog;